// Push Notifications Utility with Update Persistence

import { getStandaloneDebugInfo, isStandaloneLaunch } from './pwaMode'

class PushNotificationManager {
  constructor(api) {
    this.api = api;
    this.registration = null;
    this.subscription = null;
    this.isInitialized = false;
    this.storageKey = 'ewert_push_subscription';
    this.lastSyncKey = 'ewert_push_last_sync';
  }

  // Check if push notifications are supported
  isSupported() {
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    
    // Check if service worker is actually registered
    let swRegistered = false;
    if (hasServiceWorker && navigator.serviceWorker.controller) {
      swRegistered = true;
    }
    
    console.log('[PUSH] Basic support check:', {
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      swRegistered,
      swController: navigator.serviceWorker?.controller?.scriptURL,
      userAgent: navigator.userAgent
    });
    
    // iOS specific checks
    const isIOS = this.isIOS();
    const isInstalled = this.isInstalled();
    const iosVersion = this.getIOSVersion();
    
    console.log('[PUSH] iOS check:', {
      isIOS,
      isInstalled,
      iosVersion,
      standaloneMode: getStandaloneDebugInfo()
    });
    
    if (isIOS) {
      // iOS 16.4+ required for push notifications
      if (iosVersion && iosVersion < 16.4) {
        console.log('[PUSH] iOS version too old for push notifications');
        return false;
      }
      
      // On iOS, push only works in standalone mode (installed PWA)
      const supported = hasServiceWorker && hasPushManager && hasNotification && isInstalled;
      console.log('[PUSH] iOS support result:', supported);
      return supported;
    }
    
    const supported = hasServiceWorker && hasPushManager && hasNotification;
    console.log('[PUSH] Non-iOS support result:', supported);
    return supported;
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  // Request permission for notifications
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  // Initialize push notifications with persistence
  async initialize() {
    if (this.isInitialized) {
      console.log('[PUSH] Already initialized');
      return this.subscription !== null;
    }

    if (!this.isSupported()) {
      console.warn('[PUSH] Push notifications are not supported');
      return false;
    }

    try {
      console.log('[PUSH] Initializing push notifications...');
      
      // Get service worker registration
      this.registration = await navigator.serviceWorker.ready;
      console.log('[PUSH] Service worker ready');
      
      // Check if already subscribed
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (this.subscription) {
        console.log('[PUSH] Found existing subscription');
        await this.syncWithServer();
        this.isInitialized = true;
        return true;
      } else {
        console.log('[PUSH] No existing subscription found');
        // Check if we had a subscription before (stored locally)
        const stored = this.getStoredSubscription();
        if (stored) {
          console.log('[PUSH] Found stored subscription, attempting to restore...');
          await this.restoreSubscription();
        }
      }
      
      this.isInitialized = true;
      return this.subscription !== null;
    } catch (error) {
      console.error('[PUSH] Failed to initialize push notifications:', error);
      this.isInitialized = true;
      return false;
    }
  }

  // Subscribe to push notifications with persistence
  async subscribe() {
    if (!this.registration) {
      await this.initialize();
    }

    if (!this.registration) {
      throw new Error('Service worker not available');
    }

    try {
      // Request permission if not granted
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Permission not granted for notifications');
      }

      // Subscribe to push manager
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.getVapidPublicKey())
      });

      // Store subscription locally for persistence
      this.storeSubscription(this.subscription);
      
      // Send subscription to server
      await this.syncWithServer();

      console.log('[PUSH] Successfully subscribed to push notifications');
      return true;
    } catch (error) {
      console.error('[PUSH] Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    if (!this.subscription) {
      return true;
    }

    try {
      // Unsubscribe from push manager
      await this.subscription.unsubscribe();
      
      // Notify server
      await this.api.post('api/notifications/push/unsubscribe');
      
      // Clear local storage
      this.clearStoredSubscription();
      await this.clearAppBadge();
      
      this.subscription = null;
      console.log('[PUSH] Successfully unsubscribed from push notifications');
      return true;
    } catch (error) {
      console.error('[PUSH] Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  // Test push notification
  async testNotification() {
    try {
      await this.api.post('api/notifications/push/test');
      console.log('Test notification sent');
      return true;
    } catch (error) {
      console.error('Failed to send test notification:', error);
      return false;
    }
  }

  // Get VAPID public key (in production, this should come from server)
  getVapidPublicKey() {
    // Updated VAPID public key - matches server
    return 'BByHlWkBRHYPsbTTHGaS0sDuqSiKU6Kw4UU8XZYZVALPXlXWEmqREpY1thAw9Ms6X1lLVy4ApZoBVDvZH0bcr7U';
  }

  // Convert VAPID key to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Check if running on iOS
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  // Get iOS version
  getIOSVersion() {
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (match) {
      const major = parseInt(match[1], 10);
      const minor = parseInt(match[2], 10);
      const patch = parseInt(match[3] || '0', 10);
      return major + (minor / 10) + (patch / 100);
    }
    return null;
  }

  // Check if PWA is installed (standalone mode)
  isInstalled() {
    return isStandaloneLaunch();
  }

  // Get support status with detailed info
  getSupportInfo() {
    const isIOS = this.isIOS();
    const isInstalled = this.isInstalled();
    const iosVersion = this.getIOSVersion();
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    
    console.log('[PUSH] getSupportInfo called:', {
      isIOS,
      isInstalled,
      iosVersion,
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      userAgent: navigator.userAgent
    });
    
    if (isIOS) {
      if (iosVersion && iosVersion < 16.4) {
        return {
          supported: false,
          reason: 'ios_version_too_old',
          message: `Push уведомления требуют iOS 16.4+. У вас iOS ${iosVersion.toFixed(1)}.`
        };
      }
      
      if (!isInstalled) {
        return {
          supported: false,
          reason: 'ios_not_installed',
          message: 'На iOS push уведомления работают только в установленном приложении. Добавьте приложение на домашний экран через Safari → Поделиться → "Добавить на экран Домой".'
        };
      }
    }
    
    if (!hasServiceWorker) {
      return {
        supported: false,
        reason: 'no_service_worker',
        message: 'Браузер не поддерживает Service Workers.'
      };
    }
    
    if (!hasPushManager) {
      return {
        supported: false,
        reason: 'no_push_manager',
        message: 'Браузер не поддерживает Push API.'
      };
    }
    
    if (!hasNotification) {
      return {
        supported: false,
        reason: 'no_notification',
        message: 'Браузер не поддерживает Notification API.'
      };
    }
    
    return {
      supported: true,
      reason: 'supported',
      message: 'Push уведомления поддерживаются.'
    };
  }

  // Show local notification (for testing)
  showLocalNotification(title, options = {}) {
    if (!this.isSupported()) return;

    const defaultOptions = {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      data: {
        dateOfArrival: Date.now()
      }
    };

    // Don't add vibrate on iOS
    if (!this.isIOS()) {
      defaultOptions.vibrate = [100, 50, 100];
    }

    new Notification(title, { ...defaultOptions, ...options });
  }

  // Check if app icon badging is supported by the current browser/PWA runtime.
  isBadgeSupported() {
    return typeof navigator !== 'undefined' && (
      'setAppBadge' in navigator ||
      'clearAppBadge' in navigator
    );
  }

  // Set the app icon badge. On iOS this works for installed Home Screen web apps.
  async setAppBadge(count = 1) {
    const normalizedCount = Math.max(0, Number(count) || 0);

    if (normalizedCount === 0) {
      return this.clearAppBadge();
    }

    try {
      if ('setAppBadge' in navigator) {
        await navigator.setAppBadge(normalizedCount);
        return true;
      }

      this.postBadgeMessage('SET_APP_BADGE', normalizedCount);
      return false;
    } catch (error) {
      console.error('[PUSH] Failed to set app badge:', error);
      return false;
    }
  }

  // Clear the app icon badge and keep the service worker badge store in sync.
  async clearAppBadge() {
    try {
      if ('clearAppBadge' in navigator) {
        await navigator.clearAppBadge();
      }

      this.postBadgeMessage('CLEAR_APP_BADGE');
      return true;
    } catch (error) {
      console.error('[PUSH] Failed to clear app badge:', error);
      return false;
    }
  }

  async syncAppBadge(count) {
    if (Number(count) > 0) {
      return this.setAppBadge(count);
    }

    return this.clearAppBadge();
  }

  postBadgeMessage(type, count) {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const message = { type };
    if (typeof count !== 'undefined') {
      message.count = count;
    }

    const controller = navigator.serviceWorker.controller;
    if (controller) {
      controller.postMessage(message);
      return;
    }

    navigator.serviceWorker.ready
      .then((registration) => {
        const worker = registration.active || registration.waiting || registration.installing;
        if (worker) {
          worker.postMessage(message);
        }
      })
      .catch((error) => {
        console.error('[PUSH] Failed to post badge message to service worker:', error);
      });
  }

  // Store subscription locally for persistence
  storeSubscription(subscription) {
    try {
      const subscriptionData = {
        subscription: subscription.toJSON(),
        timestamp: Date.now(),
        vapidKey: this.getVapidPublicKey()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(subscriptionData));
      console.log('[PUSH] Subscription stored locally');
    } catch (error) {
      console.error('[PUSH] Failed to store subscription:', error);
    }
  }

  // Get stored subscription
  getStoredSubscription() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        // Check if VAPID key matches (to detect key changes)
        if (data.vapidKey === this.getVapidPublicKey()) {
          return data;
        } else {
          console.log('[PUSH] VAPID key changed, clearing stored subscription');
          this.clearStoredSubscription();
        }
      }
    } catch (error) {
      console.error('[PUSH] Failed to get stored subscription:', error);
    }
    return null;
  }

  // Clear stored subscription
  clearStoredSubscription() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.lastSyncKey);
      console.log('[PUSH] Cleared stored subscription');
    } catch (error) {
      console.error('[PUSH] Failed to clear stored subscription:', error);
    }
  }

  // Sync subscription with server
  async syncWithServer() {
    if (!this.subscription) {
      console.log('[PUSH] No subscription to sync');
      return false;
    }

    try {
      console.log('[PUSH] Syncing subscription with server...');
      
      const response = await this.api.post('api/notifications/push/subscribe', {
        subscription: this.subscription.toJSON()
      });
      
      if (response && !response.error) {
        // Update last sync timestamp
        localStorage.setItem(this.lastSyncKey, Date.now().toString());
        console.log('[PUSH] Successfully synced with server');
        return true;
      } else {
        console.error('[PUSH] Server sync failed:', response?.error);
        return false;
      }
    } catch (error) {
      console.error('[PUSH] Failed to sync with server:', error);
      return false;
    }
  }

  // Read the browser-owned subscription and send it to the server.
  // This is intentionally independent from isInitialized so login/startup can re-register
  // the subscription after backend restarts or deployments.
  async syncCurrentSubscriptionWithServer() {
    if (!this.isSupported()) {
      return false;
    }

    try {
      this.registration = await navigator.serviceWorker.ready;
      this.subscription = await this.registration.pushManager.getSubscription();

      if (!this.subscription) {
        console.log('[PUSH] No current browser subscription to sync');
        return false;
      }

      this.storeSubscription(this.subscription);
      await this.syncWithServer();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('[PUSH] Failed to sync current subscription with server:', error);
      return false;
    }
  }

  // Restore subscription after app update
  async restoreSubscription() {
    const stored = this.getStoredSubscription();
    if (!stored) {
      console.log('[PUSH] No stored subscription to restore');
      return false;
    }

    try {
      console.log('[PUSH] Attempting to restore subscription...');
      
      // Try to get current subscription first
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (!this.subscription) {
        console.log('[PUSH] No current subscription, attempting to resubscribe...');
        
        // Check if permission is still granted
        if (Notification.permission !== 'granted') {
          console.log('[PUSH] Permission no longer granted, cannot restore');
          this.clearStoredSubscription();
          return false;
        }
        
        // Resubscribe with same VAPID key
        this.subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.getVapidPublicKey())
        });
        
        console.log('[PUSH] Resubscribed successfully');
      }
      
      // Update stored subscription with current one
      this.storeSubscription(this.subscription);
      
      // Sync with server
      await this.syncWithServer();
      
      console.log('[PUSH] Subscription restored successfully');
      return true;
    } catch (error) {
      console.error('[PUSH] Failed to restore subscription:', error);
      this.clearStoredSubscription();
      return false;
    }
  }

  // Check if subscription needs to be restored (called after app updates)
  async checkAndRestoreSubscription() {
    if (!this.isSupported()) {
      return false;
    }

    try {
      await this.initialize();
      
      // If we have a subscription, make sure it's synced with server
      if (this.subscription) {
        const lastSync = localStorage.getItem(this.lastSyncKey);
        const now = Date.now();
        const syncAge = lastSync ? now - parseInt(lastSync) : Infinity;
        
        // Sync if it's been more than 1 hour since last sync
        if (syncAge > 60 * 60 * 1000) {
          console.log('[PUSH] Subscription needs re-sync with server');
          await this.syncWithServer();
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[PUSH] Failed to check/restore subscription:', error);
      return false;
    }
  }

  // Get subscription status
  getSubscriptionStatus() {
    return {
      isSupported: this.isSupported(),
      isSubscribed: this.subscription !== null,
      permission: Notification.permission,
      hasStoredSubscription: this.getStoredSubscription() !== null,
      lastSync: localStorage.getItem(this.lastSyncKey)
    };
  }
}

export default PushNotificationManager;
